<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <header>
    <div class="headline">
      <h1>{{ 'Lab_Tests_Breadcrumb_Title' | translateWF: 'Laboratory Tests' }}</h1>
    </div>
    <div class="buttons">
      @if (isCreating()) {
        <p-splitbutton [label]="'Save' | translateWF: 'Save'" (onClick)="save(false)" [disabled]="!newTestComposition.value" [model]="saveMenuItems" />
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="cancel()" />
      } @else {
        <p-splitbutton [label]="'Save' | translateWF: 'Save'" [disabled]="true" [model]="saveMenuItems" />
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="cancel()" />
      }
    </div>
  </header>

  <div class="content">
    @if (!isCreating()) {
      <!-- Search Section -->
      <p-panel header="Production Card">
        <ng-template pTemplate="icons">
          <p-autocomplete
            class="w-[380px]"
            styleClass="w-full"
            inputStyleClass="w-full"
            #productionCardSearch="cslServerSearch"
            [cslServerSearch]="fetchProductionCards"
            [suggestions]="productionCardSearch.results()"
            [placeholder]="'Lab_Tests_SearchPlaceholder' | translateWF: 'Search production card'"
            [showClear]="true"
            [dropdown]="true"
            (onSelect)="onProductionCardSelect($event)"
            optionLabel="productionCardNumber"
          />
        </ng-template>
      </p-panel>

      <!-- Production Card Info -->
      @if (productionCard()) {
        <p-panel header="Information">
          <div class="production-card-info">
            <app-individual-production-card-general-information [showOrderInfo]="true" [showDyeingInfo]="true" [showManufacturerTable]="false" [showBalanceTable]="false" />
          </div>
        </p-panel>

        <!-- Tests Section -->
        <p-panel [header]="'Lab_Tests_TestsHeading' | translateWF: 'Tests'">
          <ng-template pTemplate="icons">
            <p-select
              [options]="standards()"
              optionLabel="composition"
              [placeholder]="'Lab_Tests_SelectStandardPlaceholder' | translateWF: 'Select Standard'"
              [formControl]="selectedStandard"
              appendTo="body"
            />
            <p-button [label]="'Lab_Tests_CreateTestButton' | translateWF: 'Create Test'" icon="pi pi-plus" (onClick)="startCreateTest()" [disabled]="!selectedStandard.value" />
          </ng-template>

          <!-- Tests Table grouped by composition -->
          @for (group of groupedTests(); track group.composition) {
            <p-panel data-tone="surface" [header]="group.composition">
              <p-card data-variant="compact">
                <p-table
                  [value]="group.tests"
                  [scrollable]="true"
                  styleClass="cs-datatable-dense cs-datatable-cell-top cs-datatable-min-w"
                  [style]="{ '--cs-datatable-min-w': '1600px' }"
                >
                  <ng-template pTemplate="header">
                    <tr class="cs-datatable-header-dark">
                      <th>{{ 'Lab_Tests_Col_Status' | translateWF: 'Status' }}</th>
                      <th>{{ 'Lab_Tests_Col_Width' | translateWF: 'Width' }}</th>
                      <th>{{ 'Lab_Tests_Col_WeightG' | translateWF: 'Weight (g)' }}</th>
                      <th>{{ 'Lab_Tests_Col_ShrinkageLW' | translateWF: 'Shrinkage L/W' }}</th>
                      <th>{{ 'Lab_Tests_Col_TorquePercent' | translateWF: 'Torque %' }}</th>
                      <th>{{ 'Lab_Tests_Col_CPI' | translateWF: 'CPI' }}</th>
                      <th>{{ 'Lab_Tests_Col_WPI' | translateWF: 'WPI' }}</th>
                      <th>{{ 'Lab_Tests_Col_BurstingPSI' | translateWF: 'Bursting PSI' }}</th>
                      <th>{{ 'Lab_Tests_Col_Pilling' | translateWF: 'Pilling' }}</th>
                      <th>{{ 'Lab_Tests_Col_PH' | translateWF: 'PH' }}</th>
                      <th>{{ 'Lab_Tests_Col_WaterFastnessShort' | translateWF: 'Water Fast.' }}</th>
                      <th>{{ 'Lab_Tests_Col_PerspFastnessShort' | translateWF: 'Persp. Fast.' }}</th>
                      <th>{{ 'Lab_Tests_Col_CrockingDW' | translateWF: 'Crocking D/W' }}</th>
                      <th>{{ 'Lab_Tests_Col_Illumination' | translateWF: 'Illumination' }}</th>
                      <th>{{ 'Actions' | translateWF: 'Actions' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-test>
                    <tr>
                      <!-- Status -->
                      <td>
                        <span
                          class="status-badge"
                          [class.passed]="test.passed === true && !test.hardApproval"
                          [class.failed]="test.passed === false"
                          [class.pending]="test.passed === null || test.passed === undefined"
                          [class.hard-approved]="test.hardApproval"
                          >{{ getStatusLabel(test) }}</span
                        >
                      </td>
                      <!-- Width Inches - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[0]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[0]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[0]?.isWithinSpec === null || test.parameters[0]?.isWithinSpec === undefined"
                          >{{ test.parameters[0]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Weight - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[1]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[1]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[1]?.isWithinSpec === null || test.parameters[1]?.isWithinSpec === undefined"
                          >{{ test.parameters[1]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Shrinkage (Length/Width) - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[2]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[2]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[2]?.isWithinSpec === null || test.parameters[2]?.isWithinSpec === undefined"
                          >{{ test.parameters[2]?.actualValue?.value || '-' }}</span
                        >
                        /
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[3]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[3]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[3]?.isWithinSpec === null || test.parameters[3]?.isWithinSpec === undefined"
                          >{{ test.parameters[3]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Torque - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[4]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[4]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[4]?.isWithinSpec === null || test.parameters[4]?.isWithinSpec === undefined"
                          >{{ test.parameters[4]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- CPI - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[5]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[5]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[5]?.isWithinSpec === null || test.parameters[5]?.isWithinSpec === undefined"
                          >{{ test.parameters[5]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- WPI - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[6]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[6]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[6]?.isWithinSpec === null || test.parameters[6]?.isWithinSpec === undefined"
                          >{{ test.parameters[6]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Bursting - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[7]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[7]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[7]?.isWithinSpec === null || test.parameters[7]?.isWithinSpec === undefined"
                          >{{ test.parameters[7]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Pilling - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[8]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[8]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[8]?.isWithinSpec === null || test.parameters[8]?.isWithinSpec === undefined"
                          >{{ test.parameters[8]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- PH - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[9]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[9]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[9]?.isWithinSpec === null || test.parameters[9]?.isWithinSpec === undefined"
                          >{{ test.parameters[9]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Water Fastness - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[10]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[10]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[10]?.isWithinSpec === null || test.parameters[10]?.isWithinSpec === undefined"
                          >{{ test.parameters[10]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Perspiration Fastness - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[11]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[11]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[11]?.isWithinSpec === null || test.parameters[11]?.isWithinSpec === undefined"
                          >{{ test.parameters[11]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Crocking (Dry/Wet) - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[12]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[12]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[12]?.isWithinSpec === null || test.parameters[12]?.isWithinSpec === undefined"
                          >{{ test.parameters[12]?.actualValue?.value || '-' }}</span
                        >
                        /
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[13]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[13]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[13]?.isWithinSpec === null || test.parameters[13]?.isWithinSpec === undefined"
                          >{{ test.parameters[13]?.actualValue?.value || '-' }}</span
                        >
                      </td>
                      <!-- Primary Illumination - actualValue -->
                      <td>
                        <span
                          class="value-text"
                          [class.within-spec]="test.parameters[14]?.isWithinSpec === true"
                          [class.out-of-spec]="test.parameters[14]?.isWithinSpec === false"
                          [class.no-spec]="test.parameters[14]?.isWithinSpec === null || test.parameters[14]?.isWithinSpec === undefined"
                        >
                          {{ test.parameters[14]?.illuminationValue?.actualForColors ?? '-' }} /
                          {{ test.parameters[14]?.illuminationValue?.actualForWhites ?? '-' }}
                        </span>
                      </td>
                      <!-- Actions -->
                      <td class="actions-cell">
                        <p-button
                          class="mr-4"
                          icon="pi pi-trash"
                          severity="danger"
                          [outlined]="true"
                          size="small"
                          [pTooltip]="'Lab_Tests_DeleteTestTooltip' | translateWF: 'Delete Test'"
                          (onClick)="deleteTest(test)"
                        />
                        @if (canHardApprove(test)) {
                          <p-button
                            icon="pi pi-check-circle"
                            severity="success"
                            [outlined]="true"
                            size="small"
                            [pTooltip]="'Lab_Tests_HardApprove_Tooltip' | translate"
                            tooltipPosition="left"
                            (onClick)="confirmHardApprove(test)"
                          />
                        }
                        @if (test.hardApproval) {
                          <span class="hard-approved-badge" [pTooltip]="getHardApprovedTooltip(test)">
                            <i class="pi pi-shield"></i>
                          </span>
                        }
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="15" class="text-center py-4">
                        {{
                          'Lab_Tests_EmptyComposition'
                            | translateWF
                              : 'No tests
                  found for this composition'
                        }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </p-card>
            </p-panel>
          }

          @if (groupedTests().length === 0 && productionCard()) {
            <div class="no-tests">
              <p>{{ 'Lab_Tests_EmptyForCard' | translateWF: 'No tests found for this production card.' }}</p>
              <p>{{ 'Lab_Tests_EmptyForCardHint' | translateWF: 'Click "Create Test" to add a new test.' }}</p>
            </div>
          }
        </p-panel>
      }
    } @else {
      <!-- Create New Test Form -->
      <div class="col">
        <p-panel header="Information">
          <p-card>
            <div class="grid-auto-4 items-end">
              <div class="form-box">
                <label>{{ 'Lab_Tests_CustomerLabel' | translateWF: 'Customer' }}</label>
                <input class="w-full" pInputText [value]="customerName()" [disabled]="true" />
              </div>
              <div class="form-box">
                <label>{{ 'Lab_Tests_StandardLabel' | translateWF: 'Standard' }}</label>
                <input class="w-full" pInputText [value]="selectedStandardForTest()?.composition" [disabled]="true" />
              </div>
              <div class="form-row">
                <label>{{ 'Lab_Tests_CompositionLabel' | translateWF: 'Composition' }}</label>
                <input class="w-full" pInputText [formControl]="newTestComposition" [placeholder]="'Lab_Tests_CompositionPlaceholder' | translateWF: 'Enter composition'" />
              </div>
            </div>
          </p-card>
        </p-panel>

        <p-panel [header]="'Lab_Tests_TestDetailsHeading' | translateWF: 'Test Details'">
          <p-card data-variant="compact">
            <p-table [value]="[1]" [scrollable]="true" styleClass="cs-datatable-dense cs-datatable-cell-top cs-datatable-min-w" [style]="{ '--cs-datatable-min-w': '1600px' }">
              <ng-template pTemplate="header">
                <tr class="cs-datatable-header-dark">
                  <th></th>
                  <th>{{ 'Lab_Tests_Form_WidthInches' | translateWF: 'Width Inches' }}</th>
                  <th>{{ 'Lab_Tests_Col_WeightG' | translateWF: 'Weight (g)' }}</th>
                  <th>{{ 'Lab_Tests_Form_ShrinkageL' | translateWF: 'Shrinkage L' }}</th>
                  <th>{{ 'Lab_Tests_Form_ShrinkageW' | translateWF: 'Shrinkage W' }}</th>
                  <th>{{ 'Lab_Tests_Form_Torque' | translateWF: 'Torque' }}</th>
                  <th>{{ 'Lab_Tests_Form_CPI' | translateWF: 'CPI ±1' }}</th>
                  <th>{{ 'Lab_Tests_Form_WPI' | translateWF: 'WPI ±1' }}</th>
                  <th>{{ 'Lab_Tests_Form_Bursting' | translateWF: 'Bursting' }}</th>
                  <th>{{ 'Lab_Tests_Col_Pilling' | translateWF: 'Pilling' }}</th>
                  <th>{{ 'Lab_Tests_Col_PH' | translateWF: 'PH' }}</th>
                  <th>{{ 'Lab_Tests_Form_WaterFastness' | translateWF: 'Water Fastness' }}</th>
                  <th>{{ 'Lab_Tests_Form_PerspFastness' | translateWF: 'Persp. Fastness' }}</th>
                  <th>{{ 'Lab_Tests_Form_CrockingDry' | translateWF: 'Crocking Dry' }}</th>
                  <th>{{ 'Lab_Tests_Form_CrockingWet' | translateWF: 'Crocking Wet' }}</th>
                  <th>{{ 'Lab_Tests_Form_IllumColors' | translateWF: 'Illum. Colors' }}</th>
                  <th>{{ 'Lab_Tests_Form_IllumWhites' | translateWF: 'Illum. Whites' }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body">
                <!-- Standard Values Row (readonly) -->
                <tr class="standard-row">
                  <td>
                    <strong>{{ 'Lab_Tests_StandardRowLabel' | translateWF: 'Standard' }}</strong>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(0) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(1) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(2) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(3) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(4) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(5) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(6) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(7) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(8) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(9) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(10) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(11) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(12) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatStandardValue(13) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatIlluminationStandard(true) }}</span>
                  </td>
                  <td>
                    <span class="standard-value">{{ formatIlluminationStandard(false) }}</span>
                  </td>
                </tr>
                <!-- Actual Values Row (editable) -->
                <tr class="actual-row">
                  <td>
                    <strong>{{ 'Lab_Tests_ActualRowLabel' | translateWF: 'Actual' }}</strong>
                  </td>
                  <!-- Width Inches -->
                  <td>
                    @if (hasStandardValue(0)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.width" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Weight -->
                  <td>
                    @if (hasStandardValue(1)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.weight" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Shrinkage Length -->
                  <td>
                    @if (hasStandardValue(2)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.shrinkageLength" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Shrinkage Width -->
                  <td>
                    @if (hasStandardValue(3)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.shrinkageWidth" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Torque -->
                  <td>
                    @if (hasStandardValue(4)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.torque" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- CPI -->
                  <td>
                    @if (hasStandardValue(5)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.cpi" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- WPI -->
                  <td>
                    @if (hasStandardValue(6)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.wpi" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Bursting -->
                  <td>
                    @if (hasStandardValue(7)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.bursting" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Pilling -->
                  <td>
                    @if (hasStandardValue(8)) {
                      <input pInputText type="number" class="param-input" placeholder="0" [(ngModel)]="formData.pilling" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- PH -->
                  <td>
                    @if (hasStandardValue(9)) {
                      <input pInputText class="param-input" placeholder="0" [(ngModel)]="formData.ph" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Water Fastness -->
                  <td>
                    @if (hasStandardValue(10)) {
                      <input pInputText class="param-input" placeholder="0" [(ngModel)]="formData.waterFastness" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Perspiration Fastness -->
                  <td>
                    @if (hasStandardValue(11)) {
                      <input pInputText class="param-input" placeholder="0" [(ngModel)]="formData.perspirationFastness" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Crocking Dry -->
                  <td>
                    @if (hasStandardValue(12)) {
                      <input pInputText class="param-input" placeholder="0" [(ngModel)]="formData.crockingDry" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Crocking Wet -->
                  <td>
                    @if (hasStandardValue(13)) {
                      <input pInputText class="param-input" placeholder="0" [(ngModel)]="formData.crockingWet" />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Illumination Colors -->
                  <td>
                    @if (hasIlluminationStandardValue(true)) {
                      <p-select
                        [options]="illuminationOptions"
                        [(ngModel)]="formData.illuminationColors"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="'Select' | translateWF: 'Select'"
                        styleClass="cs-select-fixed-sm"
                        appendTo="body"
                      />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                  <!-- Illumination Whites -->
                  <td>
                    @if (hasIlluminationStandardValue(false)) {
                      <p-select
                        [options]="illuminationOptions"
                        [(ngModel)]="formData.illuminationWhites"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="'Select' | translateWF: 'Select'"
                        styleClass="cs-select-fixed-sm"
                        appendTo="body"
                      />
                    } @else {
                      <input pInputText class="param-input" [disabled]="true" value="-" />
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>
      </div>
    }
  </div>
</main>

<p-toast position="top-center" />
<p-confirmDialog />
